import {closeCurrentDialog, openDialog, showNotification} from 'platform/components';
import {Box, HStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {equals, isNil, not} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {RequestItem} from '@dms/api/shared';
import {
  DeletePartsRequestReturnItemsRequest,
  PartsRequestReturnBasketItem,
  PostPartsRequestReturnItemRequest,
  useDeletePartsRequestReturnItemsMutation,
  useGetPartsRequestReturnBasketQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePatchPartsRequestReturnBasketItemQuantityMutation,
  usePostPartsRequestReturnItemMutation,
  usePutWarehouseHeaderDynamicActionsMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {
  createHeaderActions,
  EitherQuantityOrError,
  FullScreenModal,
  handleApiError,
  refreshDmsDatagrid,
  useBasketItemsQuantityState,
  WAREHOUSE_GRID_CODES,
} from '@dms/shared';

import {composePath, Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {useBasketMechanic} from '../../hooks/useBasketMechanic';
import {AfterSalesMaterialReturnBasket} from '../AfterSalesMaterialReturnBasket/AfterSalesMaterialReturnBasket';
import {EmptyServiceOrderReturnDialog} from './components/EmptyServiceOrderReturnDialog';
import {ServiceOrderReturnMaterialList} from './components/ServiceOrderReturnMaterialList';

interface ServiceOrderReturnMaterialModalProps extends RequiredTestIdProps {
  serviceOrderReturnId: string;
  authorizationProfileId: string | Nullish;
  onSubmit: VoidFunction;
  isFromServiceOrder?: boolean;
}
export function ServiceOrderReturnMaterialModal(props: ServiceOrderReturnMaterialModalProps) {
  const navigate = useNavigate();

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetPartsRequestReturnBasketQuery({originEntityHeaderId: props.serviceOrderReturnId});

  const {
    data: partsRequestActions,
    isLoading: isPartsRequestActionsLoading,
    isError: isPartsRequestActionsError,
  } = useGetWarehouseHeaderDynamicActionsQuery({
    id: props.serviceOrderReturnId,
    resource: 'parts-request-return',
  });

  const [addItemsToBasket, {isLoading: isAddingItemsToBasket}] =
    usePostPartsRequestReturnItemMutation();

  const [patchBasketItemQuantity, {isLoading: isChangingQuantity}] =
    usePatchPartsRequestReturnBasketItemQuantityMutation();

  const [deleteBasketItems, {isLoading: isDeletingBasketItems}] =
    useDeletePartsRequestReturnItemsMutation();

  const [putPartsRequestAction, {isLoading: isPartsRequestActionLoading}] =
    usePutWarehouseHeaderDynamicActionsMutation({
      fixedCacheKey: 'sor-basket-action',
    });

  const basketItems = basket?.requestReturnBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const basketTotalPrice = basket?.requestReturnBasketTotalPrice;

  const basketMechanicId = basket?.assignMechanicId;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItemsToBasket ||
    isPartsRequestActionLoading ||
    isDeletingBasketItems ||
    isChangingQuantity;

  const {setBasketItemQuantity, hasInvalidBasketItems, hasUnsettledQuantity} =
    useBasketItemsQuantityState(basketItems);

  const {
    mechanics,
    areMechanicsLoading,
    mechanicPayload,
    inputMechanicId,
    mechanicError,
    setMechanicError,
    handleMechanicChange,
  } = useBasketMechanic({
    authorizationProfileId: props.authorizationProfileId,
    assignedMechanicId: basketMechanicId,
  });

  const isLoading = isBasketLoading || isPartsRequestActionsLoading;

  const hasError = hasBasketError || isPartsRequestActionsError;

  const handleAddToBasket = async (selectedItems: RequestItem[]) => {
    const requestBody: PostPartsRequestReturnItemRequest['body'] = {
      originEntityHeaderId: props.serviceOrderReturnId,
      originEntityHeaderType: 'service-order-return',
      originEntityType: 'svc_job_item',
      requestItems: selectedItems,
    };

    await addItemsToBasket({
      body: requestBody,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setBasketItemQuantity(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await patchBasketItemQuantity({
      originEntityHeaderId: props.serviceOrderReturnId,
      requestItemId: itemId,
      body: {quantity: quantity.newQuantity},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleDelete = async (itemsIds: string | string[]) => {
    const ids = isArray(itemsIds) ? itemsIds : [itemsIds];

    const requestBody: DeletePartsRequestReturnItemsRequest['body'] = {
      requestItemId: ids,
    };

    await deleteBasketItems({
      originEntityHeaderId: props.serviceOrderReturnId,
      body: requestBody,
    })
      .unwrap()
      .then(() => refreshDmsDatagrid('warehouse-service-order-return-select-items'))
      .catch(handleApiError);
  };

  const handleActionClick = (actionKey: string) => {
    if ((actionKey === 'request' || actionKey === 'complete') && isNil(mechanicPayload)) {
      return setMechanicError(i18n.t('general.notifications.fieldIsRequired'));
    }

    if (actionKey === 'discard') {
      return putPartsRequestAction({
        id: props.serviceOrderReturnId,
        version: 'v2',
        resource: 'parts-request-return',
        body: {
          actionKey,
          originEntityHeaderType: 'service-order-return',
        },
      })
        .unwrap()
        .then((result) => {
          if (result.deleteEmptySorPrompt) {
            return openDialog(
              <EmptyServiceOrderReturnDialog
                onDelete={() => handleResolveDecision(actionKey, 'DELETE')}
                onDontDelete={() => handleResolveDecision(actionKey, 'DONT_DELETE')}
              />,
              {id: EMPTY_SOR_DIALOG, size: 'small'}
            );
          }
          props.onSubmit();
        })
        .catch(handleApiError);
    }

    return putPartsRequestAction({
      id: props.serviceOrderReturnId,
      version: 'v2',
      resource: 'parts-request-return',
      body: {
        actionKey,
        returnedBy: mechanicPayload?.id,
        originEntityHeaderType: 'service-order-return',
      },
    })
      .unwrap()
      .then(() => {
        if (actionKey === 'complete') {
          showNotification.success(i18n.t('entity.warehouse.notifications.returnCompleted'));
        }

        refreshDmsDatagrid(WAREHOUSE_GRID_CODES.warehouseServiceOrderPartsReturnItem);
      })
      .then(() => props.onSubmit?.())
      .catch(handleApiError);
  };

  const handleResolveDecision = (actionKey: string, decision: 'DELETE' | 'DONT_DELETE') =>
    match(decision)
      .with('DELETE', () => {
        putPartsRequestAction({
          id: props.serviceOrderReturnId,
          version: 'v2',
          resource: 'parts-request-return',
          body: {
            actionKey,
            originEntityHeaderType: 'service-order-return',
            deleteEmptySorPromptResponseCode: decision,
          },
        })
          .unwrap()
          .then(() => showNotification.success(i18n.t('general.notifications.successfullyDeleted')))
          .then(() => {
            closeCurrentDialog();
            props.onSubmit();
            if (props.isFromServiceOrder) {
              // User should stay on service order detail after discarding (workshop specific).
              return;
            }
            navigate(composePath(warehouseRoutes.serviceOrderReturns));
          })
          .catch(handleApiError);
      })
      .with('DONT_DELETE', () => {
        putPartsRequestAction({
          id: props.serviceOrderReturnId,
          version: 'v2',
          resource: 'parts-request-return',
          body: {
            actionKey,
            originEntityHeaderType: 'service-order-return',
            deleteEmptySorPromptResponseCode: decision,
          },
        })
          .unwrap()
          .then(() => {
            closeCurrentDialog();
            props.onSubmit();
          })
          .catch(handleApiError);
      })
      .exhaustive();

  const dynamicActions = createHeaderActions({
    actions: partsRequestActions?.actions,
    callback: handleActionClick,
    isLoading: isPartsRequestActionLoading,
    isDisabled: isBasketEmpty || hasPendingRequest || hasUnsettledQuantity,
  });

  return (
    <FullScreenModal
      headline={i18n.t('general.actions.returnMaterial')}
      headerActions={dynamicActions}
    >
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <ServiceOrderReturnMaterialList
              serviceOrderReturnId={props.serviceOrderReturnId}
              onAddToBasket={handleAddToBasket}
              data-testid={suffixTestId('list', props)}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesMaterialReturnBasket<PartsRequestReturnBasketItem>
              basket={{
                items: basketItems,
                totalPrice: basketTotalPrice,
                isLoading,
                hasError,
                hasPendingRequest,
                hasInvalidItems: hasInvalidBasketItems,
              }}
              mechanic={{
                mechanics,
                areMechanicsLoading,
                isMechanicRequired: true,
                selectedMechanicId: inputMechanicId,
                mechanicError,
                onMechanicChange: handleMechanicChange,
              }}
              onQuantityChange={handleQuantityChange}
              onDelete={handleDelete}
              data-testid={suffixTestId('basket', props)}
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}

const EMPTY_SOR_DIALOG = 'empty-service-order-return-dialog';
